/** Earn area: the account-style header and section bar (Overview · Rewards · Missions · Store · Referrals · Leaderboard · Benefits · Yield), Claim all, tier emblem and small building blocks (flat round glyph badges and list rows in the notification style, the glowing hero card). */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { badgeCatalog, BRAND, fmtUsd, REWARD_TIERS, type RewardTier } from '@platform/shared';
import { Glyph, type GlyphName } from '../../components/NotificationIcon';
import { cx, Icon } from '../../components/ui';
import { api } from '../../lib/api';
import { Link, usePath } from '../../lib/router';
import { useFeatureState } from '../../stores/features';
import { useMe } from '../../stores/session';
import { shortAccountId } from '../account/AccountLayout';
import { AIcon, CopyButton, errMsg } from '../account/kit';

export const EARN_TABS = [
  { to: '/earn', label: 'Overview' },
  // Volume rewards: shown only while the feature is on for the client (useEarnTabs).
  { to: '/earn/rewards', label: 'Rewards' },
  { to: '/earn/missions', label: 'Missions' },
  { to: '/earn/store', label: 'Store' },
  { to: '/earn/referrals', label: 'Referrals' },
  { to: '/earn/leaderboard', label: 'Leaderboard' },
  { to: '/earn/benefits', label: 'Benefits' },
  { to: '/earn/yield', label: 'Yield' },
] as const;

export type Msg = { ok: boolean; text: string } | null;

type EarnTab = (typeof EARN_TABS)[number];

/** The sections this client sees: Rewards only while volume rewards are on for them. */
function useEarnTabs(): readonly EarnTab[] {
  const { access } = useFeatureState();
  return EARN_TABS.filter((t) => t.to !== '/earn/rewards' || access.volume_rewards);
}

export function setTitle(t: string) {
  document.title = `${t} · ${BRAND.name}`;
}

/** Phones: one full-width button naming the current section; it opens the section list (as in My account). */
function SectionPicker({ path, tabs }: { path: string; tabs: readonly EarnTab[] }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLElement>(null);
  const current = tabs.find((t) => t.to === path);
  useEffect(() => setOpen(false), [path]);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);
  return (
    <nav ref={box} aria-label="Earn" className="relative w-full md:hidden">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="earn-sections"
        onClick={() => setOpen((o) => !o)}
        className="flex h-12 w-full items-center justify-between gap-3 rounded-full bg-track pr-4 pl-5 text-[15px] font-semibold text-fg"
      >
        <span className="truncate">{current?.label ?? 'Earn'}</span>
        <Icon name="chevron" size={18} strokeWidth={2.4} className={cx('shrink-0 text-muted transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <ul id="earn-sections" className="absolute inset-x-0 top-[calc(100%+8px)] z-20 m-0 list-none rounded-2xl border border-line bg-card p-1.5 shadow-lg">
          {tabs.map((t) => {
            const on = t.to === path;
            return (
              <li key={t.to}>
                <Link
                  to={t.to}
                  onClick={() => setOpen(false)}
                  aria-current={on ? 'page' : undefined}
                  className={cx('flex h-12 items-center gap-2 rounded-xl px-4 text-[15px] font-semibold no-underline', on ? 'bg-track text-fg' : 'text-muted')}
                >
                  {t.label}
                  {on && <Icon name="check" size={18} strokeWidth={2.4} className="ml-auto" />}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </nav>
  );
}

/** Same frame as My account: who you are on the left, the section bar on the right; an optional action (Claim all) beside it. */
export function EarnLayout({ tier, action, children }: { tier?: RewardTier; action?: ReactNode; children: ReactNode }) {
  const path = usePath().replace(/\/$/, '') || '/earn';
  const me = useMe();
  const tabs = useEarnTabs();
  const live = me?.accounts.find((a) => a.kind === 'live');
  const name = me ? [me.firstName, me.lastName].filter(Boolean).join(' ') || (me.contactHidden ? 'Client' : me.email) : '';
  const initials = me ? (me.firstName && me.lastName ? me.firstName[0]! + me.lastName[0]! : me.email.slice(0, 2)).toUpperCase() : '';
  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-bg text-[14px]">
      <div className="mx-auto flex w-full max-w-[1720px] flex-col gap-8 px-4 py-8 md:px-10 xl:px-[100px] xl:py-12">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          {me && (
            <div className="flex min-w-0 items-center gap-3.5">
              <div className="grid size-12 shrink-0 place-items-center rounded-full bg-accent-bg text-base font-semibold text-accent-fg">{initials}</div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-[17px] font-semibold">{name}</span>
                  {tier && (
                    <span className="rounded-md px-2 py-0.5 text-[12px] font-medium" style={{ background: `color-mix(in srgb, ${tier.color} 18%, transparent)`, color: tier.color }}>
                      {tier.name}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-2 text-[13px] text-muted">
                  <span>@{me.username}</span>
                  {live && (
                    <>
                      <span aria-hidden>·</span>
                      <CopyButton value={live.id} kind="secondary" size="sm" className="num !h-auto !bg-transparent !p-0 !text-[13px] !font-normal text-muted hover:text-fg">
                        <span title="Copy account ID">{shortAccountId(live.id)}</span> <AIcon name="copy" size={13} />
                      </CopyButton>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="flex-1" />
          {action}
          <SectionPicker path={path} tabs={tabs} />
          <nav aria-label="Earn" className="flex overflow-x-auto max-md:hidden">
            <div className="flex gap-1 rounded-full bg-track p-1">
              {tabs.map((t) => {
                const on = t.to === path;
                return (
                  <Link
                    key={t.to}
                    to={t.to}
                    aria-current={on ? 'page' : undefined}
                    className={cx('flex shrink-0 items-center rounded-full px-5 py-2.5 text-[15px] font-semibold whitespace-nowrap no-underline', on ? 'bg-card text-fg shadow-sm' : 'text-muted hover:text-fg')}
                  >
                    {t.label}
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Claims cashback and referral rewards together into the live account. */
export function ClaimAllButton({ amount, onDone, size = 'sm', className }: { amount: number; onDone: (m: Msg) => void; size?: 'sm' | 'lg'; className?: string }) {
  const [busy, setBusy] = useState(false);
  const can = amount >= 0.01;
  return (
    <button
      type="button"
      disabled={!can || busy}
      onClick={async () => {
        setBusy(true);
        try {
          const r = await api.post<{ amount: string }>('/api/rewards/claim', { kind: 'all' });
          onDone({ ok: true, text: `${fmtUsd(r.amount)} added to your live account.` });
        } catch (e) {
          onDone({ ok: false, text: errMsg(e) });
        } finally {
          setBusy(false);
        }
      }}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-xl font-semibold whitespace-nowrap disabled:cursor-not-allowed',
        size === 'lg' ? 'h-12 px-6 text-[15px]' : 'h-10 px-5 text-[14px]',
        can ? 'bg-accent text-white' : 'bg-track text-muted',
        className,
      )}
    >
      {busy ? 'Claiming…' : can ? `Claim ${size === 'sm' ? 'all · ' : ''}${fmtUsd(String(amount))}` : 'Nothing to claim'}
    </button>
  );
}

export const tierById = (tiers: readonly RewardTier[], id: string) => tiers.find((t) => t.id === id) ?? tiers[0] ?? REWARD_TIERS[0]!;

/** Flat rounded-square emblem in the tier's colour. */
export function TierEmblem({ tier, size = 76, dim }: { tier: RewardTier; size?: number; dim?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cx('grid shrink-0 place-items-center font-bold text-black', dim && 'opacity-45')}
      style={{ width: size, height: size, borderRadius: size * 0.28, fontSize: size * 0.4, background: tier.color }}
    >
      {tier.name[0]}
    </span>
  );
}

export function TierBadge({ tier, size = 'md' }: { tier: RewardTier; size?: 'sm' | 'md' }) {
  return (
    <span
      title={tier.name}
      aria-hidden="true"
      className={cx('grid shrink-0 place-items-center rounded-lg font-bold text-black', size === 'md' ? 'size-9 text-[15px]' : 'size-6 text-[11px]')}
      style={{ background: tier.color }}
    >
      {tier.name[0]}
    </span>
  );
}

export type BadgeTone = 'base' | 'accent' | 'next' | 'done' | 'warn' | 'muted';

const BADGE_TONE: Record<BadgeTone, string> = {
  base: 'bg-track text-fg',
  accent: 'bg-accent-bg text-accent-fg',
  next: 'bg-accent text-white',
  done: 'bg-up-bg text-up',
  warn: 'bg-warn-bg text-warn',
  muted: 'bg-track text-muted',
};
const BADGE_SIZE = { xs: ['size-7', 14], sm: ['size-8', 16], md: ['size-10', 18], lg: ['size-12', 20] } as const;

/** Flat round badge with a solid glyph, as in the notification list: green when done, solid accent for what to do next. */
export function EarnBadge({ glyph, tone = 'base', size = 'md', children }: { glyph?: GlyphName; tone?: BadgeTone; size?: keyof typeof BADGE_SIZE; children?: ReactNode }) {
  const [box, px] = BADGE_SIZE[size];
  return (
    <span aria-hidden="true" className={cx('num grid shrink-0 place-items-center rounded-full text-[12px] font-semibold', box, BADGE_TONE[tone])}>
      {glyph ? <Glyph name={glyph} size={px} /> : children}
    </span>
  );
}

/** Flat Earn buttons: solid accent for the main action, an outline for the rest. */
export function btn(kind: 'primary' | 'secondary' = 'primary', size: 'sm' | 'md' | 'lg' = 'md') {
  return cx(
    'inline-flex items-center justify-center gap-2 rounded-xl font-semibold whitespace-nowrap no-underline disabled:cursor-not-allowed disabled:opacity-60',
    size === 'lg' ? 'h-12 px-6 text-[15px]' : size === 'md' ? 'h-10 px-5 text-[14px]' : 'h-9 px-4 text-[13px]',
    kind === 'primary' ? 'bg-accent text-white' : 'border border-line-strong text-fg hover:bg-track',
  );
}

/** A list row in the notification style: badge, title with an optional aside, muted text, then any action. */
export function EarnRow({ badge, title, aside, body, children, className }: { badge: ReactNode; title: ReactNode; aside?: ReactNode; body?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <div className={cx('flex items-start gap-3 rounded-xl p-3', className)}>
      {badge}
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <div className="flex items-baseline gap-2.5">
          <span className="min-w-0 text-[14.5px] leading-snug font-semibold">{title}</span>
          {aside && <span className="num ml-auto shrink-0 text-[12px] text-muted">{aside}</span>}
        </div>
        {body && <div className="text-[13px] leading-[1.45] text-muted">{body}</div>}
        {children}
      </div>
    </div>
  );
}

/** "Open referrals ›": the action under a row, as on a notification. */
export function RowLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="mt-1 inline-flex items-center gap-1 self-start text-[13px] font-semibold text-fg no-underline hover:text-accent-fg">
      {children}
      <Icon name="chevron" size={13} strokeWidth={2.4} className="-rotate-90" />
    </Link>
  );
}

export function PtsTag({ n, done }: { n: number; done?: boolean }) {
  return <span className={cx('num shrink-0 rounded-md px-2 py-0.5 text-[12px] font-semibold', done ? 'bg-up-bg text-up' : 'bg-accent-bg text-accent-fg')}>+{fmtPts(n)}</span>;
}

/** The first card of a tab: the accent glows up from the bottom edge. */
export function HeroPanel({ children, glow = '30% 125%', className }: { children: ReactNode; glow?: string; className?: string }) {
  return (
    <section
      className={cx('flex min-w-0 flex-col gap-6 overflow-hidden rounded-[28px] border border-line p-5 md:p-9', className)}
      style={{
        background: `radial-gradient(90% 75% at ${glow}, color-mix(in srgb, var(--accent) var(--earn-glow, 38%), transparent) 0%, color-mix(in srgb, var(--accent) var(--earn-glow-soft, 10%), transparent) 45%, transparent 75%), var(--card)`,
      }}
    >
      {children}
    </section>
  );
}

/** Bordered panel used across the Earn tabs. */
export function Panel({ title, icon, aside, children, className }: { title?: ReactNode; icon?: GlyphName; aside?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx('flex min-w-0 flex-col gap-5 rounded-[24px] border border-line bg-card p-5 md:p-8', className)}>
      {title && (
        <div className="flex items-center gap-2.5">
          {icon && <EarnBadge glyph={icon} tone="accent" size="sm" />}
          <h2 className="m-0 text-[18px] font-semibold">{title}</h2>
          <div className="flex-1" />
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

export function Progress({ value, max, color, label, className }: { value: number; max: number; color?: string; label: string; className?: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 100;
  return (
    <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} className={cx('h-2 w-full overflow-hidden rounded-full bg-track', className)}>
      <div className="h-full rounded-full bg-accent transition-[width]" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export const fmtPts = (n: number) => n.toLocaleString('en-US');
export const fmtPct = (n: number) => `${Number(n.toFixed(2))}%`;
export const fmtMult = (n: number) => `${Number(n.toFixed(2))}×`;

/** A badge as a small square mark (tier badges in the tier's colour, season places in gold). */
export function BadgeChip({ id, tiers, size = 'sm', count }: { id: string; tiers: readonly RewardTier[]; size?: 'sm' | 'md'; count?: number }) {
  const def = badgeCatalog(tiers).find((b) => b.id === id);
  if (!def) return null;
  const tier = id.startsWith('tier_') ? tiers.find((t) => `tier_${t.id}` === id) : undefined;
  const season = id.startsWith('season_');
  const label = `${def.name}${count && count > 1 ? ` ×${count}` : ''}: ${def.text}`;
  return (
    <span
      title={label}
      aria-label={label}
      role="img"
      className={cx(
        'num inline-grid shrink-0 place-items-center rounded-md font-bold',
        size === 'sm' ? 'h-[22px] min-w-[22px] px-1 text-[10px]' : 'size-11 rounded-full text-[13px]',
        tier ? 'text-black' : season ? 'bg-[#f5c542] text-black' : 'bg-accent-bg text-accent-fg',
      )}
      style={tier ? { background: tier.color } : undefined}
    >
      {def.mark}
    </span>
  );
}
